<template>
  <header class="bg-violet-gradient w-full px-5">
    <div class="flex h-header items-center justify-between">
      <MobileMenu />

      <div
        class="flex flex-1 gap-2 items-center lg:items-start justify-center lg:justify-start lg:flex-none"
        :class="!pageHaveEvent ? 'w-24 lg:w-[270px]' : 'w-60'"
      >
        <a :href="useRoute('dashboard')" aria-label="AirLST Logo">
          <img
            :src="Vapor.asset('images/airlst-logo-white.png')"
            alt="airlst-logo-white"
            class="mb-[4px] w-[135px] cursor-pointer"
            data-test="logo-img"
          />
        </a>

        <div
          v-if="planName"
          class="hidden lg:block text-[8px] font-medium border-[0.2px] border-[#9A8BA6] text-[#9A8BA6] py-px px-[6px] rounded-[31px] bg-[rgba(156,99,255,0.3)]"
          v-text="$t(planName)"
        />
      </div>

      <div
        class="hidden h-full flex-1 items-center justify-center gap-5 lg:flex"
      >
        <Suspense
          v-if="
            usePermission('event:list') &&
            usePage().props._user.active_company.deactivated_at === null
          "
        >
          <SwitchEvent />
        </Suspense>

        <div
          v-if="
            !isMenuEntryHidden('planner_org') &&
            (usePage().props._planner?.org?.canView === true ||
              usePage().props._planner?.org?.hidden === true ||
              usePage().props._planner?.org?.canStart === true)
          "
          :class="[
            'group mr-4 flex h-full cursor-pointer items-center gap-[6px]',
            $page.component.startsWith('Planner/Org/')
              ? 'text-white'
              : 'text-[rgba(255,255,255,0.5)] hover:text-white',
          ]"
          data-test="planner-org-link"
          @click="router.get(useRoute('planner.org.tasks', { from: 'menu' }))"
        >
          <FontAwesomeIcon
            :icon="['far', 'clipboard-list']"
            class="mb-1 text-base text-primary"
          />
          <span
            :class="[
              'mr-[2px] inline-block border-b-2 py-[8px] text-sm',
              $page.component.startsWith('Planner/Org/')
                ? 'border-primary'
                : 'whitespace-nowrap border-transparent group-hover:border-primary',
            ]"
            v-text="$t('planner.org.title')"
          />
          <span
            v-if="isPlannerTrialRunning(usePage())"
            class="rounded-full bg-white/15 px-1.5 py-px text-[11px] font-semibold text-white"
            data-test="planner-org-badge"
            v-text="$t('marketplace.state.trial')"
          />
        </div>
        <button
          v-if="
            usePermission('contact:list') &&
            hasCrmAccess() &&
            !isMenuEntryHidden('crm')
          "
          :class="[
            'group mr-2 flex h-full cursor-pointer items-center gap-[6px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
            $page.component.includes('Contacts')
              ? 'text-white'
              : 'text-[rgba(255,255,255,0.5)] hover:text-white',
          ]"
          type="button"
          data-test="address-book-icon-link"
          @click="router.get(useRoute('contacts.index'))"
        >
          <FontAwesomeIcon
            :icon="['fas', 'address-book']"
            class="mb-1 text-base text-primary"
          />
          <span
            :class="[
              'mr-[2px] inline-block border-b-2 py-[8px] text-sm',
              $page.component.includes('Contacts')
                ? 'border-primary'
                : 'whitespace-nowrap border-transparent group-hover:border-primary',
            ]"
            v-text="$t('contact.my_contacts')"
          />
          <span
            class="rounded-full bg-white/15 px-1.5 py-px text-[11px] font-semibold text-white"
            data-test="address-book-badge"
            v-text="$t('sidebar.badge.basis')"
          />
        </button>

        <button
          v-if="
            (useFeature('marketplace') || useFeature('dam')) &&
            usePermission('dam:view') &&
            !isMenuEntryHidden('dam')
          "
          :class="[
            'group mr-2 flex h-full cursor-pointer items-center gap-[6px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
            $page.component.startsWith('Dam/')
              ? 'text-white'
              : 'text-[rgba(255,255,255,0.5)] hover:text-white',
          ]"
          type="button"
          data-test="asset-library-link"
          @click="router.get(orgMediaMenuHref())"
        >
          <FontAwesomeIcon
            :icon="['fas', 'photo-film']"
            class="mb-1 text-base text-primary"
          />
          <span
            :class="[
              'mr-[2px] inline-block border-b-2 py-[8px] text-sm',
              $page.component.startsWith('Dam/')
                ? 'border-primary'
                : 'whitespace-nowrap border-transparent group-hover:border-primary',
            ]"
            v-text="$t('dam.nav.library')"
          />
          <span
            v-if="isDamTrialRunning(usePage())"
            class="rounded-full bg-white/15 px-1.5 py-px text-[11px] font-semibold text-white"
            data-test="asset-library-badge"
            v-text="$t('marketplace.state.trial')"
          />
        </button>

        <!-- Texte at organisation level, available with Marketplace or Content opt-in. -->
        <button
          v-if="
            (useFeature('marketplace') || useFeature('content')) &&
            usePermission('content:text:list')
          "
          :class="[
            'group mr-2 flex h-full cursor-pointer items-center gap-[6px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
            $page.component.startsWith('Content/')
              ? 'text-white'
              : 'text-[rgba(255,255,255,0.5)] hover:text-white',
          ]"
          type="button"
          data-test="texts-link"
          @click="router.get(orgTextsMenuHref())"
        >
          <FontAwesomeIcon
            :icon="['fas', 'file-lines']"
            class="mb-1 text-base text-primary"
          />
          <span
            :class="[
              'mr-[2px] inline-block border-b-2 py-[8px] text-sm',
              $page.component.startsWith('Content/')
                ? 'border-primary'
                : 'whitespace-nowrap border-transparent group-hover:border-primary',
            ]"
            v-text="$t('content.texts.title')"
          />
        </button>
      </div>

      <div class="flex items-center justify-end w-24 lg:w-[270px]">
        <div class="lg:mr-[16px] items-center flex relative z-11">
          <div
            v-if="pageHaveEvent && pageHaveEvent.checklist?.has_checklist"
            class="mr-[12px]"
          >
            <ChecklistDropdown
              menu-btn-class="hidden lg:block"
              menu-class="absolute right-0 lg:right-auto lg:relative"
              dropdown-class="fixed! lg:absolute!  left-2 lg:left-auto top-[80px] lg:top-auto w-[95%] lg:w-96"
              :checked="pageHaveEvent.checklist.checked_items"
              :items="useEventChecklistItems()"
            />
          </div>

          <Notifications
            menu-btn-class="hidden lg:block"
            menu-class="absolute right-0 lg:right-auto lg:relative"
            dropdown-class="fixed! lg:absolute!  left-2 lg:left-auto top-[80px] lg:top-auto w-[95%] lg:w-96"
          />

          <a
            :href="useHelpCenterUrl()"
            target="_blank"
            rel="noopener"
            class="cursor-pointer hidden lg:block"
            aria-label="Help Center"
          >
            <FontAwesomeIcon
              :icon="['fad', 'circle-question']"
              class="text-title text-primary"
            />
          </a>
        </div>

        <UserDropdown />

        <SelectLanguage dropdown-class="top-[48px] rounded-none" />
      </div>
    </div>
  </header>
</template>

<script setup>
import { orgMediaMenuHref, orgTextsMenuHref } from '@/composables/orgContent'
import SwitchEvent from '@/Components/Events/SwitchEvent.vue'
import SelectLanguage from '@/Components/Language/SelectLanguage.vue'
import Notifications from '@/Components/Notifications/Notifications.vue'
import MobileMenu from '@/Components/UserSettings/MobileMenu.vue'
import UserDropdown from '@/Components/UserSettings/UserDropdown.vue'
import { useHelpCenterUrl } from '@/composables/menu'
import { useFeature } from '@/composables/feature'
import { isMenuEntryHidden } from '@/composables/navigationPreferences'
import { usePermission } from '@/composables/permission'
import { useRoute } from '@/composables/route'
import { hasCrmAccess } from '@/composables/subscription'
import {
  isDamTrialRunning,
  isPlannerTrialRunning,
} from '@/composables/trialNavigation'
import { router, usePage } from '@inertiajs/vue3'
import Vapor from 'laravel-vapor'
import { computed } from 'vue'
import ChecklistDropdown from './ChecklistDropdown.vue'
import { useEventChecklistItems } from '@/composables/constants'

const pageHaveEvent = computed(() => usePage().props.event)

const planName = computed(() => {
  const company = usePage().props._user?.active_company

  if (!company || !company.active_subscription_plan) {
    return ''
  }

  return 'subscription.header-label.' + company.active_subscription_plan
})
</script>
